import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import API from '../utils/Api';

const initialState = {
 posts: [],
 myPosts: [],
 loading: false,
 error: null,
};

export const fetchPosts = createAsyncThunk(
 'posts/fetchPosts',
 async (_, thunkAPI) => {
   try {
     const res = await API.get('/posts');
     return res.data;
   } catch (err) {
     return thunkAPI.rejectWithValue(err.response.data.message);
   }
 }
);

export const fetchMyPosts = createAsyncThunk(
 'posts/fetchMyPosts',
 async (_, thunkAPI) => {
   try {
     const res = await API.get('/posts/me');
     return res.data;
   } catch (err) {
     return thunkAPI.rejectWithValue(err.response.data.message);
   }
 }
);

export const createPost = createAsyncThunk(
 'posts/createPost',
 async (postData, thunkAPI) => {
   try {
     let payload = postData;
     let config = {};

     if (postData instanceof FormData) {
       config = {};
     } else if (postData?.imageFile) {
       const formData = new FormData();
       if (postData.content) {
         formData.append('content', postData.content);
       }
       formData.append('image', postData.imageFile);
       payload = formData;
       config = {};
     }

     const res = await API.post('/posts', payload, config);
     return res.data;
   } catch (err) {
     return thunkAPI.rejectWithValue(err.response.data.message);
   }
 }
);

export const updatePost = createAsyncThunk(
 'posts/updatePost',
 async ({ postId, content }, thunkAPI) => {
   try {
      const res = await API.put(`/posts/${postId}`, {content});
     return res.data;
   } catch (err) {
     return thunkAPI.rejectWithValue(err.response.data.message);
   }
 });

export const toggleLike = createAsyncThunk(
 'posts/toggleLike',
 async (postId, thunkAPI) => {
   try {
     const res = await API.post(`/posts/${postId}/like`);
     return res.data;
   } catch (err) {
     return thunkAPI.rejectWithValue(err.response.data.message);
   }
 }
);

export const addComment = createAsyncThunk(
 'posts/addComment',
 async ({ postId, text, imageFile }, thunkAPI) => {
   try {
     let payload = { text };
     let config = {};

     if (imageFile) {
       const formData = new FormData();
       if (text) {
         formData.append('text', text);
       }
       formData.append('image', imageFile);
       payload = formData;
       config = {};
     }

     const res = await API.post(`/posts/${postId}/comments`, payload, config);
     return res.data;
   } catch (err) {
     return thunkAPI.rejectWithValue(err.response.data.message);
   }
 }
);

export const deletePost = createAsyncThunk(
 'posts/deletePost',
 async (postId, thunkAPI) => {
   try {
     const res = await API.delete(`/posts/${postId}`);
     return res.data;
   } catch (err) {
     return thunkAPI.rejectWithValue(err.response.data.message);
   }
 }
);

export const postSlice = createSlice({
 name: 'post',
 initialState,
 reducers: {},
 extraReducers: (builder) => {
    builder
      .addCase(fetchPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = action.payload.data;
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
        .addCase(createPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.loading = false;
        state.posts.unshift(action.payload.data);
      })
      .addCase(createPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMyPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.myPosts = action.payload.data;
      })
      .addCase(fetchMyPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = state.posts.map(post => post._id === action.meta.arg.postId ? action.payload.data : post);
        state.myPosts = state.myPosts.map(post => post._id === action.meta.arg.postId ? action.payload.data : post);
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deletePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePost.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = state.posts.filter(post => post._id !== action.meta.arg);
        state.myPosts = state.myPosts.filter(post => post._id !== action.meta.arg);
      })
      .addCase(deletePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
      
      builder
      .addCase(toggleLike.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleLike.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = state.posts.map(post => post._id === action.payload.data._id ? action.payload.data : post);
        state.myPosts = state.myPosts.map(post => post._id === action.payload.data._id ? action.payload.data : post);
      })
      .addCase(toggleLike.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = state.posts.map(post => post._id === action.payload.data._id ? action.payload.data : post);
        state.myPosts = state.myPosts.map(post => post._id === action.payload.data._id ? action.payload.data : post);
      })
      .addCase(addComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },    
});

export default postSlice.reducer;